/**
 * 開いている検討の状態と WebSocket。 正本はサーバで、 ここは届いた内容を写すだけ。
 * 手は楽観更新しない。 サーバの検証を通った手だけが木に載る。
 */
import { create } from 'zustand';
import { ensureWasm } from '../wasm';
import type { BoardTree, Cursor, PositionAnalysis, StudyMeta, StudyNode } from '../lib/study/tree';

export type StudyOp =
  | { t: 'add'; board: number; parent: string; mv: unknown }
  | { t: 'memo'; board: number; node: string; text: string }
  | { t: 'delete'; board: number; node: string }
  | { t: 'cursor'; board: number | null; node: string }
  | { t: 'analysis'; board: number; node: string; depth: number; data: PositionAnalysis };

type ServerMsg =
  | { t: 'doc'; you: string; meta: StudyMeta; trees: Record<string, BoardTree>; cursor: Cursor; peers: Peer[] }
  | { t: 'node'; board: number; node: StudyNode }
  | { t: 'cursor'; cursor: Cursor }
  | { t: 'analysis'; board: number; node: string; depth: number; data: PositionAnalysis }
  | { t: 'peers'; peers: Peer[] }
  | { t: 'error'; reason: string };

export interface Peer {
  sub: string;
  name: string;
}

/** URL が指す盤。 全体の画面なら null。 */
export function urlBoard(): number | null {
  const m = window.location.pathname.match(/^\/study\/[a-z0-9]+\/(\d+)$/);
  return m ? Number(m[1]) : null;
}

interface StudyStore {
  status: 'connecting' | 'open' | 'closed' | 'missing';
  id: string | null;
  meta: StudyMeta | null;
  trees: Record<string, BoardTree>;
  cursor: Cursor;
  /** 最初に開く盤へ移る途中。 全体の画面をはさまないために待つ。 */
  opening: boolean;
  peers: Peer[];
  you: string | null;
  error: string | null;
  /** 他の人が足した直近の手。 */
  added: { board: number; node: StudyNode } | null;
  /** 今の局面の読み。 全員に配る前の途中経過。 */
  live: { key: string; a: PositionAnalysis; running: boolean } | null;
  /** 全局解析する盤。 先頭から読む。 */
  queue: number[];
  /** 全局解析中の局面 (`board:node`)。 */
  scanning: string | null;
  connect(id: string): void;
  disconnect(): void;
  send(op: StudyOp): void;
  analyzeBoard(board: number): void;
}

const TOAST_MS = 4000;

let socket: WebSocket | null = null;
let currentId: string | null = null;
let retry: ReturnType<typeof setTimeout> | null = null;
let outbox: StudyOp[] = [];
let attempts = 0;
/** 自分が送って、 まだ戻っていない手の数。 自分の手は通知しない。 */
let ownAdds = 0;
/** ゲストのセッションは 1 度だけ確立する。 同時に発行すると別人になる。 */
let entered: Promise<unknown> | null = null;

export const useStudy = create<StudyStore>((set, get) => {
  const open = (id: string) => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${window.location.host}/ws/study/${id}`);
    socket = ws;
    ws.onopen = () => {
      attempts = 0;
      for (const op of outbox) ws.send(JSON.stringify(op));
      outbox = [];
    };
    ws.onmessage = (e) => {
      const m = JSON.parse(e.data as string) as ServerMsg;
      switch (m.t) {
        case 'doc': {
          const target = urlBoard() ?? (m.meta.boards.length === 1 ? m.meta.boards[0].board : null);
          const opening = !get().meta && m.cursor.board !== target && m.meta.boards.some((b) => b.board === target);
          set({ status: 'open', you: m.you, meta: m.meta, trees: m.trees, cursor: m.cursor, opening, peers: m.peers, error: null });
          break;
        }
        case 'node': {
          const key = String(m.board);
          const tree = get().trees[key];
          if (!tree) break;
          const isNew = !tree.nodes[m.node.id];
          const mine = isNew && ownAdds > 0;
          if (mine) ownAdds -= 1;
          set({
            trees: { ...get().trees, [key]: { ...tree, nodes: { ...tree.nodes, [m.node.id]: m.node } } },
          });
          if (isNew && !mine) {
            set({ added: { board: m.board, node: m.node } });
            setTimeout(() => set({ added: null }), TOAST_MS);
          }
          break;
        }
        case 'cursor':
          set({ cursor: m.cursor, opening: false });
          break;
        case 'analysis': {
          const key = String(m.board);
          const tree = get().trees[key];
          if (!tree) break;
          const analysis = { ...tree.analysis, [m.node]: { depth: m.depth, data: m.data } };
          set({ trees: { ...get().trees, [key]: { ...tree, analysis } } });
          break;
        }
        case 'peers':
          set({ peers: m.peers });
          break;
        case 'error':
          set({ error: m.reason, opening: false });
          break;
      }
    };
    ws.onclose = (e) => {
      if (socket !== ws || currentId !== id) return;
      socket = null;
      if (e.code === 1006 && get().meta === null && attempts > 2) {
        set({ status: 'missing' });
        return;
      }
      set({ status: 'closed' });
      attempts += 1;
      retry = setTimeout(() => open(id), Math.min(500 * 2 ** attempts, 8000));
    };
  };
  return {
    status: 'connecting',
    id: null,
    meta: null,
    trees: {},
    cursor: { board: null, node: 'root' },
    opening: false,
    peers: [],
    you: null,
    error: null,
    added: null,
    live: null,
    queue: [],
    scanning: null,
    connect(id) {
      get().disconnect();
      currentId = id;
      attempts = 0;
      set({ status: 'connecting', id, meta: null, trees: {}, added: null, error: null, live: null, queue: [], scanning: null });
      // WebSocket の前にゲストのセッションを確立する。 局面の再現に wasm も要る。
      entered ??= fetch('/api/studies/enter', { method: 'POST', credentials: 'include' });
      void Promise.allSettled([entered, ensureWasm()]).finally(() => {
        if (currentId === id && !socket) open(id);
      });
    },
    disconnect() {
      currentId = null;
      if (retry) clearTimeout(retry);
      socket?.close();
      socket = null;
      outbox = [];
      set({ meta: null });
    },
    send(op) {
      if (op.t === 'add') ownAdds += 1;
      if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(op));
      else outbox.push(op);
    },
    analyzeBoard(board) {
      if (!get().queue.includes(board)) set({ queue: [...get().queue, board] });
    },
  };
});

export interface NewStudyBoard {
  board: number;
  game_id: string;
  result: string | null;
  final_state: unknown;
  first_seat: string | null;
  moves: unknown[];
}

export async function createStudy(body: {
  title: string;
  host_seat: string | null;
  outcome: string | null;
  players: string[];
  boards: NewStudyBoard[];
  creator_seat: string | null;
}): Promise<string> {
  // 作った人を覚えるので、 セッションを先に確立する。
  entered ??= fetch('/api/studies/enter', { method: 'POST', credentials: 'include' });
  await entered;
  const res = await fetch('/api/studies', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`検討を作れませんでした (HTTP ${res.status})`);
  return ((await res.json()) as { id: string }).id;
}
